import { App } from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/services/JwtService";
import { AxiosResponse, AxiosRequestConfig } from "axios";
import store from "@/store/index";
import moment from "moment";
import { Actions } from "@/store/enums/StoreEnums";
import { handleAxiosErrorUtil } from '@/utils/AxiosUtil';
import distribuidorConfig from "@/core/config/DistribuidorConfig";
import { useConfigConcessionariaStore } from "@/pinia/concessionaria/useConfigConcessionariaStore";

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App;


  /**
   * @description decode jwt para pegar os valores do token
   */
  public static parseJwt(token) {
    let base64Url = token.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    let jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  };

  /**
   * @description valida se o token salvo ja expirou, se expirou tenta logar novamente
   */
  public static async valideTokenTime(): Promise<boolean> {

    let auth = false;
    let token = JwtService.getToken();
    if (token) {
      let tokenDecode = this.parseJwt(token);
      let time = moment().unix();
      if (time > tokenDecode.exp) {
        auth = await store.dispatch(Actions.LOGADO);
      }
      else {
        auth = true;
      }
    }

    return auth;

  };


  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {

    const host_api = store.getters.layoutConfig("distribuidor.dominio_api");
    const host_http = store.getters.layoutConfig("distribuidor.dominio_gestao") == 'localhost:8080' ? 'http' : 'https';
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);
    ApiService.vueInstance.axios.defaults.baseURL = host_http + "://" + host_api + "/api/";
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setHeader(): void {
    try{
      this.setHeaderPublic();
    }catch(error){
      console.error(error);
    }

    ApiService.vueInstance.axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${JwtService.getToken()}`;
  }


  public static setHeaderPublic(): void {
    const refreshToken = localStorage.getItem("acampa_public_refresh_token");
    const id = localStorage.getItem("acampa_public_id");

    if (refreshToken && id) {
      ApiService.vueInstance.axios.defaults.headers.common["X-Public-Session"] = refreshToken;
      ApiService.vueInstance.axios.defaults.headers.common["X-Public-Session-Id"] = id;
    }
  }

  /**
 * @description set the POST HTTP request
 * @param resource: string
 * @param params: AxiosRequestConfig
 * @returns Promise<AxiosResponse>
 */

  /**
   * @description Resolve a baseURL para o resource.
   * Prioridade: url_intergracao (quando resource bate com service_resource) > url_app > padrão do distribuidor.
   * Ambientes com ignoreConfigUrls=true (localhost, hotfix) ignoram as URLs do banco e usam sempre o padrão.
   */
  private static resolveBaseUrl(resource: string, hostApi = ''): { baseURL: string; isExternal: boolean } {
    const host = window.location.host;
    const distribuidor = distribuidorConfig.find(e => e.dominio_gestao === host);
    const ignoreConfigUrls = distribuidor?.ignoreConfigUrls === true;

    const configStore = useConfigConcessionariaStore();
    const config = ignoreConfigUrls ? null : configStore.getConfig;

    // backend envia "url_intergracao" (chave com typo preservada para compatibilidade)
    const urlIntegracao = config?.url_intergracao as string | undefined;

    // service_resource vem do backend como string JSON: '["oficinadigitalconsultardms"]'
    let serviceResources: string[] = [];
    try {
      const raw = config?.service_resource;
      if (typeof raw === 'string' && raw) {
        serviceResources = JSON.parse(raw);
      } else if (Array.isArray(raw)) {
        serviceResources = raw;
      }
    } catch {
      serviceResources = [];
    }

    // 1ª prioridade: url_intergracao quando o resource bate com service_resource
    if (urlIntegracao && serviceResources.some(r => resource.includes(r))) {
      return { baseURL: urlIntegracao, isExternal: true };
    }

    // fallback: padrão do distribuidor
    const host_api = hostApi || store.getters.layoutConfig("distribuidor.dominio_api");
    const host_http = store.getters.layoutConfig("distribuidor.dominio_gestao") == 'localhost:8080' ? 'http' : 'https';
    return { baseURL: `${host_http}://${host_api}/api/`, isExternal: false };
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static query(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params).catch((error) => {
      throw new Error(`[CAMP] ApiService ${error}`);
    });
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param slug: string
   * @returns Promise<AxiosResponse>
   */
  public static async get(resource: string, slug = "" as string, valid = true, hostApi = ''): Promise<AxiosResponse> {

    const { baseURL, isExternal } = ApiService.resolveBaseUrl(resource, hostApi);
    let axiosInstance: any = ApiService.vueInstance.axios;

    if (isExternal) {
      axiosInstance = axios.create({ baseURL });
    } else {
      ApiService.vueInstance.axios.defaults.baseURL = baseURL;
    }

    if (valid) {
      await this.valideTokenTime();
    }

    this.setHeader();

    if (axiosInstance !== ApiService.vueInstance.axios) {
      axiosInstance.defaults.headers.common = {
        ...ApiService.vueInstance.axios.defaults.headers.common,
      };
    }

    // quando isExternal a baseURL já inclui /api/, então a / inicial do resource
    // causaria axios a ignorar esse path — removemos para evitar o bug
    const resourcePath = isExternal && resource.startsWith('/') ? resource.slice(1) : resource;

    return axiosInstance
      .get(resourcePath)
      .catch((error) => {
        handleAxiosErrorUtil(error, resource);
      });

  }

  public static async getIntegracao(resource: string, slug = "" as string, valid = true): Promise<AxiosResponse> {

    const host_api = store.getters.layoutConfig("distribuidor.dominio_integracao");
    const host_http = store.getters.layoutConfig("distribuidor.dominio_gestao") == 'localhost:8080' ? 'http' : 'https';
    ApiService.vueInstance.axios.defaults.baseURL = host_api
    ApiService.vueInstance.axios.defaults.baseURL = host_http + "://" + host_api + "/api/";

    this.setHeader();
    if (valid) {
      await this.valideTokenTime();
    }

    return ApiService.vueInstance.axios
      .get(`${resource}`)
      .catch((error) => {
        throw new Error(`ErrorPath: ${resource}/${slug} ;[KT] ApiService ${error}`);
      });

  }

  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @param additionalHeaders: object
   * @returns Promise<AxiosResponse>
   */
  public static async postIntegracao(
    resource: string,
    params: AxiosRequestConfig,
    additionalHeaders = {},
    valid = true
  ): Promise<AxiosResponse> {

    const host_api = store.getters.layoutConfig("distribuidor.dominio_integracao");
    const host_http = store.getters.layoutConfig("distribuidor.dominio_gestao") == 'localhost:8080' ? 'http' : 'https';
    ApiService.vueInstance.axios.defaults.baseURL = host_api
    ApiService.vueInstance.axios.defaults.baseURL = host_http + "://" + host_api + "/api/";

    // Configure o cabeçalho Authorization
    ApiService.vueInstance.axios.defaults.headers.common["Authorization"] = `Bearer ${JwtService.getToken()}`;

    Object.assign(ApiService.vueInstance.axios.defaults.headers.common, additionalHeaders);
    this.setHeader();

    if (valid) {
      await this.valideTokenTime();
    }

    return ApiService.vueInstance.axios.post(`${resource}`, params);
  }

  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static async post(
    resource: string,
    params: AxiosRequestConfig,
    valid = true,
    hostApi = ''
  ): Promise<AxiosResponse> {

    const { baseURL, isExternal } = ApiService.resolveBaseUrl(resource, hostApi);
    let axiosInstance: any = ApiService.vueInstance.axios;

    if (isExternal) {
      axiosInstance = axios.create({ baseURL });
    } else {
      ApiService.vueInstance.axios.defaults.baseURL = baseURL;
    }

    if (valid) {
      await this.valideTokenTime();
    }

    this.setHeader();

    if (axiosInstance !== ApiService.vueInstance.axios) {
      axiosInstance.defaults.headers.common = {
        ...ApiService.vueInstance.axios.defaults.headers.common,
      };
    }

    // quando isExternal a baseURL já inclui /api/, então a / inicial do resource
    // causaria axios a ignorar esse path — removemos para evitar o bug
    const resourcePath = isExternal && resource.startsWith('/') ? resource.slice(1) : resource;

    return axiosInstance.post(resourcePath, params);
  }

  public static async postProgress(
    resource: string,
    params: AxiosRequestConfig,
    valid = true,
    onUploadProgress?: (progressEvent: ProgressEvent) => void // Callback opcional
  ): Promise<AxiosResponse> {

    const host_api = store.getters.layoutConfig("distribuidor.dominio_api");
    const host_http = store.getters.layoutConfig("distribuidor.dominio_gestao") == 'localhost:8080' ? 'http' : 'https';

    ApiService.vueInstance.use(VueAxios, axios);
    ApiService.vueInstance.axios.defaults.baseURL = `${host_http}://${host_api}/api/`;
    this.setHeader();

    if (valid) {
      await this.valideTokenTime();
    }

    const config: AxiosRequestConfig = params;
    if (onUploadProgress) {
      config.onUploadProgress = onUploadProgress;
    }

    return ApiService.vueInstance.axios.post(resource, config);
  }

  /**
   * @description send the UPDATE HTTP request
   * @param resource: string
   * @param slug: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    this.setHeader();
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params);
  }

  /**
   * @description Send the PUT HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static async put(
    resource: string,
    params: AxiosRequestConfig,
    valid = true
  ): Promise<AxiosResponse> {
    this.setHeader();
    if (valid) {
      await this.valideTokenTime();
    }

    return ApiService.vueInstance.axios.put(`${resource}`, params);
  }

  /**
   * @description Send the PATCH HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static async patch(
    resource: string,
    params: AxiosRequestConfig = {},
    valid = true
  ): Promise<AxiosResponse> {
    this.setHeader();
    if (valid) {
      await this.valideTokenTime();
    }

    return ApiService.vueInstance.axios.patch(`${resource}`, params);
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.delete(resource).catch((error) => {
      throw new Error(`[RWV] ApiService ${error}`);
    });
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static deleteV2(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.delete(resource);
  }

  public static async postXlsx(
    resource: string,
    params: AxiosRequestConfig = {},
    valid = true
  ): Promise<AxiosResponse> {
    const headers = {
      Authorization: `Bearer ${JwtService.getToken()}`,
      Accept: 'application/octet-stream'
    };
    const options: AxiosRequestConfig = {
      headers,
      responseType: 'blob'
    };

    ApiService.setHeader();
    if (valid) {
      await ApiService.valideTokenTime();
    }

    return ApiService.vueInstance.axios.post(resource, params, options);
  }

  /**
   * Faz uma requisição GET para obter um arquivo XLSX como um Blob.
   *
   * @param {string} resource - O recurso (URL) para o qual a requisição será feita.
   * @param {boolean} [valid=true] - Indica se o token deve ser validado antes de fazer a requisição.
   * @returns {Promise<Blob>} - Uma promessa que resolve com o Blob do arquivo XLSX.
   */
  public static async getXlsx(
    resource: string,
    valid = true
  ): Promise<Blob> {
    const headers = {
      Authorization: `Bearer ${JwtService.getToken()}`,
      Accept: 'application/octet-stream',
    };
    const options: AxiosRequestConfig = {
      headers,
      responseType: 'blob',
    };

    ApiService.setHeader();
    if (valid) {
      await ApiService.valideTokenTime();
    }

    const response = await ApiService.vueInstance.axios.get(resource, options);
    return response.data;
  }

  public static async postBlob(
    resource: string,
    body: any,
    valid = true,
    hostApi = ''
  ): Promise<Blob> {
    const host_api = hostApi ? hostApi : store.getters.layoutConfig("distribuidor.dominio_api");
    const host_http = store.getters.layoutConfig("distribuidor.dominio_gestao") == 'localhost:8080' ? 'http' : 'https';
  
    ApiService.vueInstance.use(VueAxios, axios);
    ApiService.vueInstance.axios.defaults.baseURL = `${host_http}://${host_api}/api/`;
    this.setHeader();
    if (valid) await this.valideTokenTime();
  
    const resp = await ApiService.vueInstance.axios.post(resource, body, {
      responseType: 'blob'
    });
    return resp.data as Blob;
  }  
}

export default ApiService;
